import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  PART_REQUEST_STATUS,
  type PartRequestStatus,
  type PartRequestWithContext,
} from '@etnrlz/shared';
import { usePartRequests, useUpdatePartRequestAny } from '../../lib/queries';
import { Badge, Button, Loading, PageHeader, Select, cx } from '../../components/ui';
import { Icon } from '../../components/icons';
import { partRequestStatusMeta } from '../../components/status';
import { RecentEmails } from '../../components/RecentEmails';
import { formatDate, formatMoney } from '../../lib/format';
import { InvoiceReviewModal } from './InvoiceReviewModal';

const COLUMNS: PartRequestStatus[] = [...PART_REQUEST_STATUS];
const NO_ORDER = '__none__';

/**
 * Área de Compras: duas visões complementares das peças solicitadas (de todas as
 * OS) — agrupadas por **invoice/pedido** (para conferir cada compra inteira) e o
 * **acompanhamento por status** (kanban). Clicar numa peça abre a tela do pedido.
 */
export function ComprasPage({ touch }: { touch?: boolean }) {
  const { data, isLoading } = usePartRequests();
  const update = useUpdatePartRequestAny();
  const navigate = useNavigate();
  const [uploadOpen, setUploadOpen] = useState(false);
  const { pathname } = useLocation();
  const base = pathname.startsWith('/shop') ? '/shop' : '/admin';
  const detailHref = (osId: string, prId: string) => `${base}/compras/${osId}/${prId}`;

  const all = data ?? [];
  const [showArquivadas, setShowArquivadas] = useState(false);
  const arquivadasCount = all.filter((p) => p.arquivada).length;
  // Visão ativa esconde as arquivadas (a menos que o usuário peça pra ver).
  const visible = showArquivadas ? all : all.filter((p) => !p.arquivada);

  const setArquivada = (pr: PartRequestWithContext, arquivada: boolean) =>
    update.mutate({ osId: pr.osId, prId: pr.id, input: { arquivada } });

  const grouped = useMemo(() => {
    const g: Record<string, PartRequestWithContext[]> = {};
    for (const c of COLUMNS) g[c] = [];
    for (const pr of visible) (g[pr.status] ??= []).push(pr);
    return g;
  }, [visible]);

  // Agrupa por OrderID (invoice). Pedidos reais primeiro; "sem pedido" por último.
  const byInvoice = useMemo(() => {
    const map = new Map<string, PartRequestWithContext[]>();
    for (const pr of visible) {
      const key = pr.orderId?.trim() || NO_ORDER;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(pr);
    }
    return [...map.entries()].sort((a, b) =>
      a[0] === NO_ORDER ? 1 : b[0] === NO_ORDER ? -1 : a[0].localeCompare(b[0]),
    );
  }, [visible]);

  const totalEntregue = all.reduce((s, p) => s + (p.valorEntregue ?? 0), 0);
  const emAberto = all.filter((p) => p.status !== 'ENTREGUE' && p.status !== 'CANCELADA').length;

  if (isLoading) return <Loading />;

  return (
    <div className="space-y-8">
      <PageHeader
        title="Compras"
        subtitle={`${all.length} peça(s) · ${emAberto} em aberto · total entregue ${formatMoney(totalEntregue)}`}
        actions={
          <>
            {arquivadasCount > 0 && (
              <Button
                variant={showArquivadas ? 'primary' : 'secondary'}
                size={touch ? 'lg' : 'md'}
                onClick={() => setShowArquivadas((v) => !v)}
              >
                <Icon name="archive" className="h-4 w-4" /> Arquivadas ({arquivadasCount})
              </Button>
            )}
            <Button onClick={() => setUploadOpen(true)} size={touch ? 'lg' : 'md'}>
              <Icon name="plus" className="h-4 w-4" /> Subir invoices / NF
            </Button>
          </>
        }
      />

      <InvoiceReviewModal open={uploadOpen} onClose={() => setUploadOpen(false)} />

      {all.length === 0 ? (
        <p className="text-sm text-muted">
          Nenhuma peça solicitada ainda. Adicione peças dentro de uma OS (Solicitação de peças).
        </p>
      ) : (
        <>
          {/* ---------------------------- Por invoice --------------------------- */}
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted mb-3 flex items-center gap-2">
              <Icon name="cart" className="h-4 w-4 text-accent" /> Por invoice / pedido
            </h2>
            <div className="space-y-3">
              {byInvoice.map(([key, items]) => (
                <InvoiceGroup
                  key={key}
                  orderId={key}
                  items={items}
                  onOpen={(pr) => navigate(detailHref(pr.osId, pr.id))}
                  onOpenPedido={key !== NO_ORDER ? () => navigate(`${base}/compras/pedido/${key}`) : undefined}
                  onArchive={setArquivada}
                />
              ))}
            </div>
          </section>

          {/* ------------------------ Acompanhamento por status ----------------- */}
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted mb-3">
              Acompanhamento por status
            </h2>
            <div className={cx('flex gap-3 overflow-x-auto pb-4', touch && 'gap-4')}>
              {COLUMNS.map((status) => {
                const meta = partRequestStatusMeta[status];
                const list = grouped[status] ?? [];
                return (
                  <div key={status} className={cx('shrink-0', touch ? 'w-80' : 'w-72')}>
                    <div className="flex items-center justify-between mb-2 px-1">
                      <div className="flex items-center gap-2 font-medium text-sm">
                        <span className="h-2 w-2 rounded-full" style={{ background: meta.color }} />
                        {meta.label}
                      </div>
                      <span className="num text-xs text-muted">{list.length}</span>
                    </div>
                    <div className="space-y-2 min-h-[60px]">
                      {list.map((pr) => (
                        <StatusCard
                          key={pr.id}
                          pr={pr}
                          color={meta.color}
                          updating={update.isPending}
                          onOpen={() => navigate(detailHref(pr.osId, pr.id))}
                          onStatus={(status) =>
                            update.mutate({ osId: pr.osId, prId: pr.id, input: { status } })
                          }
                          onArchive={() => setArquivada(pr, !pr.arquivada)}
                        />
                      ))}
                      {list.length === 0 && <p className="text-xs text-muted px-1">—</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}

      {/* Últimos e-mails recebidos (parts@) — clica e abre no Gmail. */}
      <RecentEmails />
    </div>
  );
}

/** Card de uma invoice/pedido: agrega os itens daquele OrderID com os totais. */
function InvoiceGroup({
  orderId,
  items,
  onOpen,
  onOpenPedido,
  onArchive,
}: {
  orderId: string;
  items: PartRequestWithContext[];
  onOpen: (pr: PartRequestWithContext) => void;
  onOpenPedido?: () => void;
  onArchive: (pr: PartRequestWithContext, arquivada: boolean) => void;
}) {
  const isNone = orderId === NO_ORDER;
  const moedas = new Set(items.map((i) => i.moeda));
  const moeda = moedas.size === 1 ? [...moedas][0] : undefined;
  const subtotal = items.reduce((s, i) => s + (i.valor ?? 0) * (i.quantidade ?? 1), 0);
  const frete = items.reduce((s, i) => s + (i.frete ?? 0), 0);
  const carriers = [...new Set(items.map((i) => i.transportadora).filter(Boolean))];

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-3">
        <button
          type="button"
          onClick={onOpenPedido}
          disabled={!onOpenPedido}
          className={cx(
            'flex items-center gap-2 font-medium text-left',
            onOpenPedido && 'hover:text-accent transition-colors cursor-pointer',
          )}
          title={onOpenPedido ? 'Abrir o pedido' : undefined}
        >
          <Icon name="cart" className="h-4 w-4 text-accent" />
          {isNone ? 'Sem pedido associado' : `Pedido #${orderId}`}
          <span className="text-xs text-muted">
            · {items.length} item(s){carriers.length ? ` · ${carriers.join(', ')}` : ''}
          </span>
        </button>
        {moeda && (
          <div className="text-sm">
            <span className="num">{formatMoney(subtotal, moeda)}</span>
            {frete > 0 && (
              <span className="num text-muted"> + frete {formatMoney(frete, moeda)}</span>
            )}
            <span className="num font-medium"> = {formatMoney(subtotal + frete, moeda)}</span>
          </div>
        )}
      </div>
      <ul className="divide-y divide-border/50">
        {items.map((pr) => {
          const meta = partRequestStatusMeta[pr.status];
          return (
            <li
              key={pr.id}
              onClick={() => onOpen(pr)}
              className={cx(
                'flex items-center gap-3 py-2 -mx-2 px-2 rounded cursor-pointer hover:bg-surface-2/60 transition-colors',
                pr.arquivada && 'opacity-60',
              )}
            >
              <div className="h-9 w-9 rounded bg-surface-2 overflow-hidden grid place-items-center shrink-0 border border-border">
                {pr.fotoUrl ? (
                  <img src={pr.fotoUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <Icon name="box" className="h-4 w-4 text-muted" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {pr.titulo ?? 'Peça'}
                  {pr.quantidade > 1 && <span className="text-muted text-xs ml-1">×{pr.quantidade}</span>}
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted">
                  {pr.valor != null && (
                    <span className="num text-text">{formatMoney(pr.valor, pr.moeda)}</span>
                  )}
                  {pr.codigoRastreio && (
                    <span className="inline-flex items-center gap-1">
                      <Icon name="truck" className="h-3 w-3" />
                      <span className="num">{pr.codigoRastreio}</span>
                    </span>
                  )}
                  {pr.rastreioStatus && (
                    <span className="text-text" title="Situação do rastreio">
                      {pr.rastreioStatus}
                    </span>
                  )}
                  {pr.lojaStatus && (
                    <span className="text-text" title="Status na loja (e-commerce)">
                      <span className="text-muted">loja:</span> {pr.lojaStatus}
                    </span>
                  )}
                  {pr.prazoEntrega && (
                    <span className="inline-flex items-center gap-1">
                      <Icon name="clock" className="h-3 w-3" /> {formatDate(pr.prazoEntrega)}
                    </span>
                  )}
                  {(pr.documentos?.length ?? 0) > 0 && (
                    <span className="inline-flex items-center gap-1" title="Documentos">
                      <Icon name="tag" className="h-3 w-3" /> {pr.documentos!.length}
                    </span>
                  )}
                  {isNone && pr.vehicleApelido && <span>· {pr.vehicleApelido}</span>}
                </div>
              </div>
              <Badge color={meta.color}>{meta.label}</Badge>
              {(pr.status === 'ENTREGUE' || pr.status === 'CANCELADA') && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchive(pr, !pr.arquivada);
                  }}
                  className="text-muted hover:text-accent p-1 rounded shrink-0"
                  title={pr.arquivada ? 'Restaurar' : 'Arquivar'}
                  aria-label={pr.arquivada ? 'Restaurar' : 'Arquivar'}
                >
                  <Icon name="archive" className="h-4 w-4" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Card do kanban (por status): clicável p/ abrir a peça; troca rápida de status. */
function StatusCard({
  pr,
  color,
  updating,
  onOpen,
  onStatus,
  onArchive,
}: {
  pr: PartRequestWithContext;
  color: string;
  updating: boolean;
  onOpen: () => void;
  onStatus: (status: PartRequestStatus) => void;
  onArchive: () => void;
}) {
  const canArchive = pr.status === 'ENTREGUE' || pr.status === 'CANCELADA';
  return (
    <article
      onClick={onOpen}
      className={cx(
        'card p-3 border-l-4 cursor-pointer hover:bg-surface-2/40 transition-colors',
        pr.arquivada && 'opacity-60',
      )}
      style={{ borderLeftColor: color }}
    >
      <div className="flex items-start gap-2">
        <div className="h-10 w-10 rounded bg-surface-2 overflow-hidden grid place-items-center shrink-0 border border-border">
          {pr.fotoUrl ? (
            <img src={pr.fotoUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <Icon name="box" className="h-4 w-4 text-muted" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm leading-tight truncate">{pr.titulo ?? 'Peça'}</div>
          <div className="text-xs text-muted truncate">
            {pr.vehicleApelido ?? pr.vehicleId} · {pr.osTitulo}
          </div>
        </div>
        {canArchive && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onArchive();
            }}
            disabled={updating}
            className="text-muted hover:text-accent p-1 rounded shrink-0"
            title={pr.arquivada ? 'Restaurar' : 'Arquivar'}
            aria-label={pr.arquivada ? 'Restaurar' : 'Arquivar'}
          >
            <Icon name="archive" className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted mt-2">
        {pr.valor != null && <span className="num text-text">{formatMoney(pr.valor, pr.moeda)}</span>}
        {pr.valorEntregue != null && (
          <span className="num" title="Custo entregue no Brasil">
            entregue {formatMoney(pr.valorEntregue)}
          </span>
        )}
        {pr.orderId && (
          <span className="num" title="OrderID">
            pedido #{pr.orderId}
          </span>
        )}
        {pr.link && (
          <a
            href={pr.link}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(e) => e.stopPropagation()}
            className="text-accent hover:underline inline-flex items-center gap-1"
          >
            <Icon name="link" className="h-3 w-3" /> Link
          </a>
        )}
        {pr.codigoRastreio && (
          <span className="inline-flex items-center gap-1">
            <Icon name="truck" className="h-3 w-3" />
            <span className="num">{pr.codigoRastreio}</span>
            {pr.transportadora ? ` · ${pr.transportadora}` : ''}
          </span>
        )}
        {pr.rastreioStatus && (
          <span className="text-text" title="Situação do rastreio">
            {pr.rastreioStatus}
          </span>
        )}
        {pr.lojaStatus && (
          <span className="text-text" title="Status na loja (e-commerce)">
            <span className="text-muted">loja:</span> {pr.lojaStatus}
          </span>
        )}
        {pr.prazoEntrega && (
          <span className="inline-flex items-center gap-1">
            <Icon name="clock" className="h-3 w-3" /> {formatDate(pr.prazoEntrega)}
          </span>
        )}
        {(pr.documentos?.length ?? 0) > 0 && (
          <span className="inline-flex items-center gap-1" title="Documentos">
            <Icon name="tag" className="h-3 w-3" /> {pr.documentos!.length}
          </span>
        )}
      </div>

      {/* O wrapper para o clique não abrir a tela de detalhe ao mexer no status. */}
      <div onClick={(e) => e.stopPropagation()}>
        <Select
          className="mt-2 py-1 text-sm"
          value={pr.status}
          disabled={updating}
          onChange={(e) => onStatus(e.target.value as PartRequestStatus)}
        >
          {PART_REQUEST_STATUS.map((s) => (
            <option key={s} value={s}>
              {partRequestStatusMeta[s].label}
            </option>
          ))}
        </Select>
      </div>
    </article>
  );
}
